import { Component } from 'react';
import type { default as React } from 'react';
import { connect } from 'react-redux';
import type { Dispatch } from 'redux';
import { withRouter } from 'react-router-dom';
import type { RouteComponentProps } from 'react-router-dom';
import cx from 'classnames';
import last from 'lodash/last';

// Actions
import AppActions from 'app/shared/flux/actions/AppActions';
import RouteActions from 'app/shared/flux/actions/RouteActions';
import UserItemActions from 'app/shared/flux/actions/UserItemActions';

// Components
import Animation from 'app/shared/modules/Animation';
import FloatingActionContactButton from 'app/shared/modules/hdp/ContactButtonV2/FloatingActionContactButton';
import HdpToHdpNav from 'app/shared/modules/hdp/HdpToHdpNav';
import HeaderNavMobileLogo from 'app/shared/modules/navigation/header/HeaderNavMobileLogo';
import Linker from 'app/shared/modules/Linker';
import PopupModal from 'app/shared/modules/popups/PopupModal';
import ShareModal from 'app/shared/modules/hdp/ShareModal';
import VisuallyHidden from 'app/shared/modules/VisuallyHidden';

// Type imports
import type { ListingDetails } from 'app/types/listingDetails.type';
import type { RootReduxState } from 'app/shared/flux/reducers';
import type { FilterState } from 'app/types/filter.type';

// Misc / Utils
import {
  animationClass,
  AnimationIcon,
  BackText,
  BuildingIconWrapper,
  FavButton,
  FavMenu,
  hamburgerOpenClass,
  LandingContainer,
  LeftSection,
  MenuButton,
  MenuIconWrapper,
  MobileNavNextLeft,
  MoreListingsText,
  navHardLeftClass,
  navHardLeftSSRClass,
  navHardMobileClass,
  noUserSelectClass,
  ShareButton,
  ShareIconWrapper,
  StyledListingNavBar,
  StyledVertCenterContainer,
  vertCenterLinkerClass,
  XIcon,
  zIndexTransitionClass,
} from './ListingNavBar.styles';
import animationData from 'images/animations/heart-bluetoyellowAnimation.json';
import IconBuilding from 'images/icons/building.svg';
import IconMenu from 'images/icons/dots.svg';
import IconShare from 'images/icons/share-v2.svg';
import IconX from 'images/icons/x.svg';
import { listingUtils_checkFavorite } from 'app/shared/utils/listingUtils';
import { yieldCallback } from '@zillow/yield-callback';
import {
  emitTrackHdpNativeShareEvent,
  emitTrackSaveHomeClickEvent,
  emitTrackShareHomeFromHdpEvent,
  emitTrackUnSaveHomeClickEvent,
} from 'app/shared/models/Clickstream/HdpClickstreamEvents';
import { getGlobalLogger } from '@zg-rentals/logger-base';
import { logError } from '@zg-rentals/log-error';
import type { ThunkResult } from 'app/types/redux.type';

const logger = getGlobalLogger('modules/hdp');

interface ListingNavBarOwnProps {
  isMobile?: boolean;
  onMenuToggle?: () => void;
  listing?: ListingDetails | null;
  currentListingIndex?: number | null;
  listOfHdpUrls?: Array<string>;
  contactButtonClass?: string;
  onContactFabClick?: () => void;
  isMenuOpen?: boolean;
}

interface ListingNavBarStateProps {
  existsOutsideOfList: boolean;
  filter: FilterState;
  isBuildingPage: boolean;
  isHamburgerOpen: boolean;
  isLoggedIn: boolean;
  isSSRLandingPage: boolean;
  lastVisitedListingFromListUri: string | null;
  listings: Array<ListingDetails>;
  unreadMsgCount: number;
  isMobile: boolean;
}

type ListingNavBarProps = ListingNavBarOwnProps &
  ListingNavBarStateProps &
  RouteComponentProps & {
    dispatch: {
      <T>(action: (dispatch: Dispatch, getState: () => RootReduxState) => T): T;
      (action: { type: string; payload?: unknown }): void;
    };
  };

interface ListingNavBarState {
  showShareModal: boolean;
  backUrl: string;
}

class ListingNavBar extends Component<ListingNavBarProps, ListingNavBarState> {
  savedActiveElement: HTMLElement | null = null;

  constructor(props: ListingNavBarProps) {
    super(props);
    this.state = { showShareModal: false, backUrl: this.getBackUrl() };
  }

  componentDidUpdate(prevProps: ListingNavBarProps): void {
    const { filter } = this.props;
    if (filter !== prevProps.filter) {
      // this ensures back url links to the correct SRP
      this.setBackUrl();
    }
  }

  getBackUrl = (): string => {
    const { listing, dispatch, filter } = this.props;
    const belongsToMultipleUnitBuilding = listing && listing.belongsToMultipleUnitBuilding;

    // byCoords and byMaloneLotId agree that the unit comes from a building AND we aren't on a building page
    if (belongsToMultipleUnitBuilding && !listing.building && this.belongsToBuildingBasedOnFilter()) {
      return dispatch(
        RouteActions.buildListingPathWithMap({
          listingUri: listing.uriBuilding,
          keepMapLocation: true,
          filter,
        }) as ThunkResult<string>,
      );
    } else {
      return dispatch(RouteActions.getAreaPathWithMapData() as ThunkResult<string>);
    }
  };

  setBackUrl = (): void => {
    this.setState({ backUrl: this.getBackUrl() });
  };

  // byCoords call determined the listing was a building (based on filter)
  belongsToBuildingBasedOnFilter = (): boolean => {
    const { listing, listings } = this.props;

    if (!listings.length) {
      // short circuit logic (and warning) until listings.listingGroups.byCoords is set
      return false;
    }

    const matchedListingInList = listings.filter((summary) => {
      return listing && summary.maloneLotIdEncoded === listing.maloneLotIdEncoded;
    });

    if (matchedListingInList.length === 0) {
      logger?.info('ListingNavBar - could not find listing in byCoords list');

      return false;
    } else {
      return matchedListingInList[0].building;
    }
  };

  handleOpenHamburgerNav = yieldCallback((): void => {
    const { dispatch } = this.props;

    this.savedActiveElement = document.activeElement as HTMLElement | null;
    dispatch(AppActions.activateHamburger() as ThunkResult<void>);
  });

  handleCloseHamburgerNav = (): void => {
    const { dispatch } = this.props;

    if (this.savedActiveElement) {
      this.savedActiveElement.focus();
    }

    dispatch(AppActions.deactivateHamburger() as ThunkResult<void>);
    this.savedActiveElement = null;
  };

  yieldFavorite = yieldCallback((listing: ListingDetails): void => {
    const { dispatch } = this.props;
    dispatch(UserItemActions.toggleUserItem('favorite', listing) as ThunkResult<void>);
  });

  handleFavorite = yieldCallback((): void => {
    const { listing } = this.props;
    const isFavorited = listingUtils_checkFavorite(listing);

    if (!isFavorited) {
      emitTrackSaveHomeClickEvent({
        listing,
        triggerLocation: 'home_details',
        triggerObject: 'home_details_component|action_bar',
      });
    } else {
      emitTrackUnSaveHomeClickEvent({
        listing,
        triggerLocation: 'home_details',
        triggerObject: 'home_details_component|action_bar',
      });
    }

    if (listing) {
      this.yieldFavorite(listing);
    }
  });

  yieldShareClick = yieldCallback((): void => {
    emitTrackShareHomeFromHdpEvent();

    if (navigator.share) {
      // Web Share API is supported
      navigator
        .share({
          title: 'HotPads listing',
          text: 'Check out this listing I found on HotPads!',
          url: window.location.href,
        })
        .then(() => {
          emitTrackHdpNativeShareEvent();
          logger?.info('Successfully shared via Web Share API');
          return;
        })
        .catch((err) => {
          logError({ error: err, context: 'Error sharing via Web Share API' });
        });
    } else {
      // Web Share API not supported - use ShareModal
      this.setState({ showShareModal: true });
    }
  });

  handleShareClick = (): void => {
    this.yieldShareClick();
  };

  handleCloseShareModal = yieldCallback((): void => {
    this.setState({ showShareModal: false });
  });

  renderBack = (): JSX.Element => {
    const { isSSRLandingPage, isMobile, isBuildingPage, listing, unreadMsgCount = 0 } = this.props;
    const { backUrl } = this.state;
    const isBuilding = (listing && listing.building) || isBuildingPage;
    const belongsToMultipleUnitBuilding = listing && this.belongsToBuildingBasedOnFilter();
    const isNotBuildingOrBelongsToBuilding = !isBuilding && !belongsToMultipleUnitBuilding;

    const yieldRouterTransition = yieldCallback((): void => {
      window.router.transitionTo(backUrl);
    });

    const handleLinkerClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
      // Prevent the browser from going through with its default <a> behavior
      e.preventDefault();

      // Use react-router to navigate user back to the last screen
      yieldRouterTransition();
    };

    if (isSSRLandingPage) {
      return (
        <LandingContainer>
          <Linker className={cx(navHardLeftSSRClass, vertCenterLinkerClass)} to={backUrl} onClick={handleLinkerClick}>
            <MoreListingsText>More Listings</MoreListingsText>
          </Linker>
          {isMobile && (
            <MobileNavNextLeft>
              <HeaderNavMobileLogo
                onClick={this.handleOpenHamburgerNav}
                onCloseHamburgerNav={this.handleCloseHamburgerNav}
                unreadMsgCount={unreadMsgCount}
              />
            </MobileNavNextLeft>
          )}
        </LandingContainer>
      );
    } else {
      return (
        <Linker
          className={cx(navHardLeftClass, {
            [navHardMobileClass]: isMobile,
          })}
          to={backUrl}
          onClick={handleLinkerClick}
          data-group
        >
          {isBuilding && (isMobile ? <XIcon src={IconX} alt="close" /> : <BackText>Close</BackText>)}
          {!isBuilding && belongsToMultipleUnitBuilding && <BackText>Back to building</BackText>}
          {isNotBuildingOrBelongsToBuilding &&
            (isMobile ? <XIcon src={IconX} alt="close" /> : <BackText>Close</BackText>)}
        </Linker>
      );
    }
  };

  shouldShowHdpToHdp = (): boolean => {
    const { isSSRLandingPage, listing } = this.props;

    const hasMultipleUnitsAfterFilter = listing && this.belongsToBuildingBasedOnFilter();

    if (isSSRLandingPage) {
      return false;
    }

    if (listing && listing.building) {
      return true;
    }

    if (listing && listing.belongsToMultipleUnitBuilding && hasMultipleUnitsAfterFilter) {
      return false;
    }

    return true;
  };

  render(): JSX.Element | null {
    const {
      currentListingIndex,
      existsOutsideOfList,
      isBuildingPage,
      isHamburgerOpen,
      isLoggedIn,
      isMenuOpen,
      lastVisitedListingFromListUri,
      listing,
      listOfHdpUrls,
      onMenuToggle,
      contactButtonClass,
      onContactFabClick,
    } = this.props;
    const { showShareModal } = this.state;

    if (!listing) {
      return null;
    }

    const isBuilding = (listing && listing.building) || isBuildingPage;
    const isFavorited = listingUtils_checkFavorite(listing);

    const showHdpToHdpnav = this.shouldShowHdpToHdp();

    return (
      <StyledListingNavBar
        className={cx({
          [hamburgerOpenClass]: isHamburgerOpen,
          [zIndexTransitionClass]: !isHamburgerOpen,
        })}
      >
        <LeftSection>
          {!existsOutsideOfList && !lastVisitedListingFromListUri && this.renderBack()}

          {showHdpToHdpnav && (
            <HdpToHdpNav
              currentListingIndex={currentListingIndex}
              existsOutsideOfList={existsOutsideOfList}
              listOfHdpUrls={listOfHdpUrls}
              lastVisitedListingFromListUri={lastVisitedListingFromListUri}
            />
          )}
        </LeftSection>
        <StyledVertCenterContainer>
          {isBuilding ? (
            <BuildingIconWrapper src={IconBuilding} alt="" />
          ) : (
            <FavMenu>
              <FavButton
                className={noUserSelectClass}
                onClick={this.handleFavorite}
                aria-pressed={isFavorited ? 'true' : 'false'}
              >
                <AnimationIcon>
                  <Animation
                    className={animationClass}
                    animationData={animationData}
                    shouldLoop={false}
                    shouldAutoplay={false}
                    shouldUseLastFrame={isFavorited}
                    isInteractive={isLoggedIn}
                  />
                </AnimationIcon>
                <VisuallyHidden>Favorite</VisuallyHidden>
              </FavButton>
              <ShareButton id="ListingNavBar-share" expanded={showShareModal} onClick={this.handleShareClick}>
                <ShareIconWrapper src={IconShare} alt="share listing" fetchpriority="low" decoding="async" />
              </ShareButton>
              <MenuButton
                id="ListingNavBar-menu"
                expanded={isMenuOpen}
                onClick={onMenuToggle}
                aria-expanded={isMenuOpen ? 'true' : 'false'}
              >
                <MenuIconWrapper src={IconMenu} alt="listing menu" fetchpriority="low" decoding="async" />
              </MenuButton>
            </FavMenu>
          )}
        </StyledVertCenterContainer>
        {showShareModal && (
          <PopupModal
            aria-labelledby="dialog-title"
            onHidePopup={this.handleCloseShareModal}
            paddingInline="4x"
            position="top"
            slideUp
          >
            <ShareModal />
          </PopupModal>
        )}
        <FloatingActionContactButton contactButtonClass={contactButtonClass} onContactFabClick={onContactFabClick} />
      </StyledListingNavBar>
    );
  }
}

const mapStateToProps = (
  state: RootReduxState,
  ownProps: ListingNavBarOwnProps & RouteComponentProps,
): ListingNavBarStateProps => {
  const isSSRLandingPage =
    state.app.isInitialSsrPage && ownProps.location.pathname === state.location.ssrEntry.pathname;

  // listing.isBuilding is not available early enough for the skeleton nav bar
  const isBuildingPage = last(ownProps.location.pathname.split('/')) === 'building';

  return {
    existsOutsideOfList: state.currentListingDetails.existsOutsideOfList,
    filter: state.filter,
    isBuildingPage,
    isHamburgerOpen: state.app.hamburger,
    isLoggedIn: state.user.loggedIn,
    isMobile: state.app.device.screenWidth === 'sm',
    isSSRLandingPage,
    lastVisitedListingFromListUri: state.currentListingDetails.lastVisitedListingFromListUri,
    listings: state.listings.listingGroups.byCoords,
    unreadMsgCount: state.user.unreadMsgCount,
  };
};

export default withRouter(connect(mapStateToProps)(ListingNavBar));
